/*
 * EN TOUT PREMIER, et l'ordre compte : ce module retire le laissez-passer de
 * l'adresse avant que l'import de `./router` ne crée le routeur, qui capture
 * `window.location` — fragment compris. Voir `identite/entreePrecoce.ts`.
 */
import './identite/entreePrecoce'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { router } from './router'
import { installerAnnoncesRoute } from './router/annonces'
import { allerALaPorte, consommerRetour } from './identite/porte'
import { i18n } from './i18n'
import { setAccesRetireHandler, setUnauthorizedHandler } from './api/client'
import { useAuthStore } from './stores/auth'
import { initThemeEager } from './stores/theme'
import { initA11yEager } from './stores/a11y'

// Polices auto-hébergées. Le socle CSS déclare « Inter » et « JetBrains Mono »
// avec repli sur la pile système : sans ces imports l'application reste lisible,
// mais n'a pas l'allure de betool. Seuls les poids réellement utilisés sont
// importés — en ajouter alourdirait le bundle pour rien.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/jetbrains-mono/400.css'
import './assets/main.css'
import { ecouterPerteIdentite } from './identite/propagation'
import { installerRaccourciDeconnexion } from './identite/raccourciDeconnexion'
import { ouvrirRuntimeIdentite } from './identite/runtime'
import { surveillerSlot } from './identite/surveillance'

// Thème et accessibilité appliqués avant le montage : le script en ligne de
// index.html couvre la première peinture, ceux-ci couvrent la suite. Une police
// qui grossit APRÈS la première peinture fait sauter toute la mise en page sous
// les yeux de l'utilisateur.
initThemeEager()
initA11yEager()

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(i18n)
/*
 * `app.use(router)` est VOLONTAIREMENT absent d'ici : il vit dans `demarrer()`.
 * Voir le commentaire de cette fonction — l'installer ici lance la navigation
 * initiale, et la garde tranche avant que le laissez-passer ne soit échangé.
 */

// APRÈS pinia et i18n : le crochet résout le store d'accessibilité et traduit le
// titre à chaque navigation, donc les deux doivent être installés.
installerAnnoncesRoute(router)

// Le client HTTP ne connaît ni Pinia ni le routeur : il signale simplement
// « la session n'est plus valable » et c'est ici qu'on décide quoi en faire.
// Ce câblage doit venir APRÈS `app.use(pinia)`, sans quoi `useAuthStore()`
// n'aurait pas d'instance active à résoudre.
setUnauthorizedHandler(() => {
  const auth = useAuthStore()
  if (!auth.isAuthenticated) return
  auth.forgetLocally()
  // À la PORTE : cette application n'a plus d'écran de connexion à viser, et
  // `redirect` n'a plus de destinataire. Le laissez-passer ramènera ici de
  // toute façon — c'est l'origine qui est enregistrée, pas un chemin.
  allerALaPorte()
})

// Coupure d'accès signature (E9-7) : le 403 typé `signature-non-activee` peut
// frapper N'IMPORTE quelle requête console au chargement — sans rappel dédié,
// chaque écran afficherait sa propre erreur. On route vers UN écran sobre.
// Anti-boucle : rejouée depuis l'écran lui-même (sa liste fondatrice refuse),
// la navigation ne se ré-enfile pas.
setAccesRetireHandler(() => {
  if (router.currentRoute.value.name !== 'console.acces-retire') {
    void router.push({ name: 'console.acces-retire' })
  }
})

// SORTIE MUTUELLE (addendum 5, D9) : le trousseau est unique et partagé. Se
// déconnecter dans le widget — ici, dans un autre onglet, ou dans une autre
// application du tenant — retire le slot pour tout le monde. Sans cette
// écoute, cette page garderait sa propre session : des écrans pleins, et le
// droit de travailler, alors que l'identité qui les autorisait n'existe plus.
//
// Même geste que le 401 : on coupe SANS appeler le serveur (le jeton est déjà
// sans valeur) et on renvoie à la connexion. Les deux chemins de sortie mènent
// donc au même endroit, ce qui est la seule façon qu'ils ne divergent pas.
const runtimeIdentite = ouvrirRuntimeIdentite()
if (runtimeIdentite !== null) {
  const sortir = (): void => {
    const auth = useAuthStore()
    if (!auth.isAuthenticated) return
    auth.forgetLocally()
    allerALaPorte()
  }

  // Le SIGNAL : immédiat, mais limité à cette origine — un `BroadcastChannel`
  // ne traverse pas de `kiosk.betool.fr` à `certif.betool.fr`.
  ecouterPerteIdentite(runtimeIdentite, { fermer: sortir })

  // Le BALAYAGE : la seule protection quand le retrait vient d'une AUTRE
  // application du tenant. Il interroge le BFF, seule source que les deux
  // partagent. Les deux mécanismes sont nécessaires, et aucun ne remplace
  // l'autre.
  surveillerSlot(runtimeIdentite, {
    fermer: sortir,
    estConnecte: () => useAuthStore().isAuthenticated,
  })
}

// Ctrl+Q : la déconnexion au clavier, pour les postes qu'on quitte vite. Elle
// passe par `logout()` — donc par la révocation serveur — et non par la
// coupure locale : ici, quelqu'un DEMANDE de partir, il ne subit pas une perte
// d'identité. Le raccourci est un confort, pas une garantie : certains
// navigateurs gardent Ctrl+Q pour eux et la page ne le voit jamais.
installerRaccourciDeconnexion({
  estConnecte: () => useAuthStore().isAuthenticated,
  deconnecter: async () => {
    await useAuthStore().logout()
    allerALaPorte()
  },
})

// Revalidation en tâche de fond : l'interface s'affiche immédiatement avec la
// session locale, et un jeton révoqué entre-temps (compte désactivé, mot de
// passe changé sur un autre poste) déclenche la déconnexion dès la réponse.
// Volontairement non bloquant — attendre le réseau pour peindre le premier
// écran est ce qui donne l'impression d'une application lente.
void useAuthStore(pinia).refreshUser()

/*
 * LE RETOUR DE LA PORTE, AVANT QUE LE ROUTEUR NE DÉMARRE.
 *
 * On revient de la page de connexion avec un laissez-passer dans le fragment.
 * Il faut l'échanger AVANT que la garde de routes ne tranche : sinon elle
 * constate « pas de session », renvoie à la porte — qui renverrait ici, et
 * ainsi de suite. Et une redirection interne perdrait le fragment en chemin.
 *
 * ─── POURQUOI `app.use(router)` EST ICI, ET NON EN HAUT DU FICHIER ──────────
 *
 * Parce qu'attendre avant `mount()` ne suffit PAS, et que le croire a coûté
 * plusieurs déploiements. `app.use(router)` ne se contente pas d'enregistrer le
 * routeur : `router.install()` lance IMMÉDIATEMENT la navigation initiale. La
 * garde lit donc `currentSession()` au premier tic de la page, pendant que
 * l'échange est encore sur le réseau — elle ne voit rien, redirige vers
 * `/login`, et `mount()` ne fait plus qu'afficher une route déjà tranchée.
 *
 * Mesuré en production par le banc de parcours : session posée (684 octets,
 * jamais effacée), `POST /api/betool/session` en 200, `GET /auth/me` en 200 —
 * et retour à la porte quand même. Le rechargement suivant entrait sans
 * problème, ce qui rendait le défaut invisible à toute vérification manuelle.
 *
 * Installer le routeur ICI est ce qui rend l'attente réelle : la première
 * décision de navigation est prise sur l'état d'APRÈS l'échange. Le backoffice
 * a le même piège sous une autre forme — `createWebHistory` y capture l'URL à
 * la création du routeur, d'où son `entreePrecoce.ts`.
 *
 * C'est le SEUL endroit du démarrage qui attend le réseau, et seulement quand
 * il y a un laissez-passer à échanger : un chargement ordinaire ne paie rien.
 */
async function demarrer(): Promise<void> {
  // Sans laissez-passer, la fonction rend false sans toucher au reseau.
  if (await consommerRetour()) await useAuthStore(pinia).refreshUser()
  app.use(router)
  app.mount('#app')
}

void demarrer()
